import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { CircularProgress, IconButton, makeStyles, useTheme } from "@material-ui/core";
import { DeleteOutline, Mic, Pause, Send } from "@material-ui/icons";
import { i18n } from "../../translate/i18n";

// Gravador de voz no padrão do WhatsApp Web: a barra de digitação vira lixeira, tempo, onda
// ao vivo, pausar/continuar e enviar.

const WA_RED = "#ea0038";
const WA_GREEN = "#00a884";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const icon = isDark ? "#aebac1" : "#54656f";
  return {
    bar: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      zIndex: 12,
      display: "flex",
      alignItems: "center",
      gap: 6,
      background: isDark ? "#202c33" : "#f0f2f5",
    },
    button: {
      flex: "none",
      width: 42,
      height: 42,
      padding: 9,
      borderRadius: "50%",
      color: icon,
      "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(11,20,26,0.07)" },
      "&.Mui-disabled": { color: icon, opacity: 0.38 },
      "& .MuiSvgIcon-root": { fontSize: 26 },
    },
    pause: {
      color: WA_RED,
    },
    clock: {
      flex: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      minWidth: 62,
      color: isDark ? "#e9edef" : "#111b21",
      fontSize: 15,
      fontVariantNumeric: "tabular-nums",
    },
    dot: {
      flex: "none",
      width: 10,
      height: 10,
      borderRadius: "50%",
      backgroundColor: WA_RED,
      animation: "$pulse 1.2s ease-in-out infinite",
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    },
    dotPaused: {
      animation: "none",
      backgroundColor: icon,
    },
    "@keyframes pulse": {
      "0%, 100%": { opacity: 1 },
      "50%": { opacity: 0.25 },
    },
    wave: {
      flex: 1,
      minWidth: 0,
      height: 34,
      display: "block",
    },
    send: {
      flex: "none",
      width: 42,
      height: 42,
      padding: 9,
      borderRadius: "50%",
      backgroundColor: WA_GREEN,
      color: "#ffffff",
      "&:hover": { backgroundColor: "#008f72" },
      "&.Mui-disabled": { backgroundColor: isDark ? "#2a3942" : "#c5ccd1", color: "#ffffff" },
      "& .MuiSvgIcon-root": { fontSize: 22, marginLeft: 3 },
    },
  };
});

const formatClock = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
};

const BAR_WIDTH = 3;
const BAR_GAP = 2;
const SAMPLE_EVERY_MS = 70;

const AudioRecorderBar = ({ recorder, busy, onCancel, onSend }) => {
  const classes = useStyles();
  const theme = useTheme();
  const canvasRef = useRef(null);
  const pausedRef = useRef(false);
  const clockRef = useRef({ total: 0, since: Date.now() });
  const [paused, setPaused] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const waveColor = theme.palette.type === "dark" ? "#8696a0" : "#667781";

  // A biblioteca de gravação não tem "pausar"; o que ela expõe é o AudioContext por onde o
  // microfone passa. Suspender esse contexto para a captura, retomar continua do mesmo ponto.
  const context = recorder && recorder.context;
  const canPause = !!context && typeof context.suspend === "function" && typeof context.resume === "function";

  useEffect(() => {
    const timer = setInterval(() => {
      const clock = clockRef.current;
      const running = pausedRef.current ? 0 : Date.now() - clock.since;
      setSeconds(Math.floor((clock.total + running) / 1000));
    }, 250);
    return () => clearInterval(timer);
  }, []);

  // Onda ao vivo: um analisador sobre o mesmo fluxo do microfone, num contexto próprio (o da
  // biblioteca fica suspenso durante a pausa).
  useEffect(() => {
    const stream = recorder && recorder.activeStream;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const canvas = canvasRef.current;
    if (!stream || !AudioCtx || !canvas) return undefined;

    let audioCtx = null;
    let source = null;
    let analyser = null;
    try {
      audioCtx = new AudioCtx();
      source = audioCtx.createMediaStreamSource(stream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
    } catch (e) {
      // sem analisador a gravação segue normal, só não há onda
      if (audioCtx && audioCtx.state !== "closed") audioCtx.close().catch(() => {});
      return undefined;
    }

    const samples = new Uint8Array(analyser.fftSize);
    const levels = [];
    let frame = null;
    let lastSample = 0;

    const draw = () => {
      const ratio = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (!width || !height) return;
      if (canvas.width !== Math.round(width * ratio) || canvas.height !== Math.round(height * ratio)) {
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
      }
      const ctx = canvas.getContext("2d");
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = waveColor;
      const step = BAR_WIDTH + BAR_GAP;
      const capacity = Math.floor(width / step);
      if (levels.length > capacity) levels.splice(0, levels.length - capacity);
      // a barra mais nova entra pela direita e a onda corre para a esquerda
      for (let i = 0; i < levels.length; i += 1) {
        const barHeight = Math.max(3, Math.round(levels[levels.length - 1 - i] * height));
        const x = width - (i + 1) * step + BAR_GAP;
        const y = (height - barHeight) / 2;
        if (ctx.roundRect) {
          ctx.beginPath();
          ctx.roundRect(x, y, BAR_WIDTH, barHeight, BAR_WIDTH / 2);
          ctx.fill();
        } else {
          ctx.fillRect(x, y, BAR_WIDTH, barHeight);
        }
      }
    };

    const tick = (now) => {
      frame = requestAnimationFrame(tick);
      if (pausedRef.current || now - lastSample < SAMPLE_EVERY_MS) return;
      lastSample = now;
      analyser.getByteTimeDomainData(samples);
      // pico do trecho (não a média): fala baixa ainda desenha barras visíveis
      let peak = 0;
      for (let i = 0; i < samples.length; i += 1) {
        const v = Math.abs(samples[i] - 128) / 128;
        if (v > peak) peak = v;
      }
      levels.push(Math.min(1, Math.pow(peak, 0.7) * 1.25));
      draw();
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      try {
        source.disconnect();
      } catch (e) {
        /* já desconectado */
      }
      if (audioCtx.state !== "closed") audioCtx.close().catch(() => {});
    };
  }, [recorder, waveColor]);

  const togglePause = async () => {
    if (!canPause || busy) return;
    const clock = clockRef.current;
    try {
      if (pausedRef.current) {
        await context.resume();
        clock.since = Date.now();
        pausedRef.current = false;
        setPaused(false);
      } else {
        await context.suspend();
        clock.total += Date.now() - clock.since;
        pausedRef.current = true;
        setPaused(true);
      }
    } catch (e) {
      /* o navegador recusou: segue gravando/pausado como estava */
    }
  };

  // Parar a gravação com o contexto suspenso depende do navegador: retoma antes de encerrar.
  const finish = async (action) => {
    if (pausedRef.current && canPause) {
      try {
        await context.resume();
      } catch (e) {
        /* segue assim mesmo */
      }
    }
    action();
  };

  return (
    <div className={classes.bar} role="group" aria-label={i18n.t("conversation.recorder.label")}>
      <IconButton
        className={classes.button}
        aria-label={i18n.t("conversation.recorder.discard")}
        title={i18n.t("conversation.recorder.discard")}
        disabled={busy}
        onClick={() => finish(onCancel)}
      >
        <DeleteOutline />
      </IconButton>
      <span className={classes.clock}>
        <span className={clsx(classes.dot, paused && classes.dotPaused)} aria-hidden="true" />
        <span role="timer" aria-label={i18n.t("conversation.recorder.time")}>
          {formatClock(seconds)}
        </span>
      </span>
      <canvas ref={canvasRef} className={classes.wave} aria-hidden="true" data-recorder-wave="true" />
      {canPause && (
        <IconButton
          className={clsx(classes.button, classes.pause)}
          aria-label={paused ? i18n.t("conversation.recorder.resume") : i18n.t("conversation.recorder.pause")}
          title={paused ? i18n.t("conversation.recorder.resume") : i18n.t("conversation.recorder.pause")}
          aria-pressed={paused}
          disabled={busy}
          onClick={togglePause}
        >
          {paused ? <Mic /> : <Pause />}
        </IconButton>
      )}
      <IconButton
        className={classes.send}
        aria-label={i18n.t("conversation.recorder.send")}
        title={i18n.t("conversation.recorder.send")}
        disabled={busy}
        onClick={() => finish(onSend)}
      >
        {busy ? <CircularProgress size={20} color="inherit" /> : <Send />}
      </IconButton>
    </div>
  );
};

export default AudioRecorderBar;
